/**
 * Command Center Context
 * Manages shared state for the Command Center master-detail layout
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from './AppContext';
import { useAuth } from './AuthContext';
import {
    normalizeObjective,
    generateInsights,
    calculateTrend
} from '../utils/objectiveHelpers';
import { getDateRange, DATE_RANGE_PRESETS } from '../utils/helpers';
import {
    loadLatestAdvisorChat,
    saveAdvisorChat,
    prepareChatDataForSave
} from '../services/advisorChatService';
import { buildCampaignKnowledgeBase } from '../services/campaignKnowledgeService';

const CommandCenterContext = createContext();

export const useCommandCenter = () => {
    const context = useContext(CommandCenterContext);
    if (!context) {
        throw new Error('useCommandCenter must be used within CommandCenterProvider');
    }
    return context;
};

const API_BASE = 'http://localhost:3001';

export const CommandCenterProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();

    // Selection state
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState(null);

    // Date range state
    const [dateRangePreset, setDateRangePreset] = useState(DATE_RANGE_PRESETS.LAST_7_DAYS);
    const [dateRange, setDateRange] = useState(() => getDateRange(DATE_RANGE_PRESETS.LAST_7_DAYS));

    // Data state
    const [campaigns, setCampaigns] = useState([]);
    const [previousPeriodData, setPreviousPeriodData] = useState({});
    const [platformConnections, setPlatformConnections] = useState([]);
    const [aggregateMetrics, setAggregateMetrics] = useState(null);
    const [previousAggregateMetrics, setPreviousAggregateMetrics] = useState(null);
    const [monthlyBudget, setMonthlyBudget] = useState(null);

    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Expanded sections state (for detail view)
    const [expandedSections, setExpandedSections] = useState({
        audiences: false,
        creatives: false,
        placements: false,
        scheduledChanges: false,
        googleAds: false
    });

    // AI Advisor chat state (shared between Command Center and Deep Dive)
    const [advisorMessages, setAdvisorMessages] = useState([]);
    const [advisorGoal, setAdvisorGoal] = useState(null);
    const [advisorChatId, setAdvisorChatId] = useState(null);
    const [advisorLoading, setAdvisorLoading] = useState(false);

    // Update date range when preset changes
    useEffect(() => {
        const range = getDateRange(dateRangePreset);
        setDateRange(range);
    }, [dateRangePreset]);

    // Fetch platform connections
    const fetchPlatformConnections = useCallback(async () => {
        if (!currentUser || !selectedBrand) return;

        try {
            const response = await fetch(
                `${API_BASE}/api/campaigns/connections?userId=${currentUser.uid}&brandId=${selectedBrand.id}`
            );

            if (response.ok) {
                const data = await response.json();
                setPlatformConnections(data.connections || []);

                // Auto-select first connected platform if none selected
                if (!selectedPlatform && data.connections?.length > 0) {
                    setSelectedPlatform(data.connections[0].platform);
                }
            } else {
                // API endpoint doesn't exist yet - use fallback from brand's connected platforms
                // or default to Meta as most common platform
                const fallbackConnections = selectedBrand.connectedPlatforms?.map(p => ({
                    platform: p.platform || p,
                    connected: true,
                    status: 'connected'
                })) || [{ platform: 'meta', connected: true, status: 'connected' }];

                setPlatformConnections(fallbackConnections);

                if (!selectedPlatform && fallbackConnections.length > 0) {
                    setSelectedPlatform(fallbackConnections[0].platform);
                }
            }
        } catch (err) {
            console.error('Error fetching platform connections:', err);
            // Fallback on error
            const fallbackConnections = selectedBrand.connectedPlatforms?.map(p => ({
                platform: p.platform || p,
                connected: true,
                status: 'connected'
            })) || [{ platform: 'meta', connected: true, status: 'connected' }];

            setPlatformConnections(fallbackConnections);

            if (!selectedPlatform && fallbackConnections.length > 0) {
                setSelectedPlatform(fallbackConnections[0].platform);
            }
        }
    }, [currentUser, selectedBrand, selectedPlatform]);

    // Fetch campaigns for the selected platform
    const fetchCampaigns = useCallback(async () => {
        if (!currentUser || !selectedBrand || !selectedPlatform) return;

        try {
            const params = new URLSearchParams({
                userId: currentUser.uid,
                brandId: selectedBrand.id,
                platform: selectedPlatform,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            const response = await fetch(`${API_BASE}/api/campaigns/list?${params}`);

            if (response.ok) {
                const data = await response.json();
                const campaignsWithObjective = (data.campaigns || []).map(campaign => ({
                    ...campaign,
                    normalizedObjective: normalizeObjective(campaign.objective)
                }));
                setCampaigns(campaignsWithObjective);
            } else {
                // API endpoint doesn't exist - use empty campaigns array
                // This allows the UI to render without data
                console.log('Campaigns API not available, using empty state');
                setCampaigns([]);
            }
        } catch (err) {
            console.error('Error fetching campaigns:', err);
            setCampaigns([]);
        }
    }, [currentUser, selectedBrand, selectedPlatform, dateRange]);

    // Fetch previous period data for trend comparisons
    const fetchPreviousPeriodData = useCallback(async () => {
        if (!currentUser || !selectedBrand || !selectedPlatform) return;

        try {
            // Calculate previous period (same duration, immediately before)
            const currentStart = new Date(dateRange.startDate);
            const currentEnd = new Date(dateRange.endDate);
            const durationMs = currentEnd - currentStart;

            const previousEnd = new Date(currentStart);
            previousEnd.setDate(previousEnd.getDate() - 1);
            const previousStart = new Date(previousEnd - durationMs);

            const formatDate = (date) => date.toISOString().split('T')[0];

            const params = new URLSearchParams({
                userId: currentUser.uid,
                brandId: selectedBrand.id,
                platform: selectedPlatform,
                startDate: formatDate(previousStart),
                endDate: formatDate(previousEnd)
            });

            const response = await fetch(`${API_BASE}/api/campaigns/list?${params}`);

            if (response.ok) {
                const data = await response.json();
                // Index by campaign ID for quick lookup
                const indexed = {};
                (data.campaigns || []).forEach(campaign => {
                    indexed[campaign.id] = campaign.currentMetrics || campaign.insights?.data?.[0] || {};
                });
                setPreviousPeriodData(indexed);
            } else {
                // API not available - use empty previous period data
                setPreviousPeriodData({});
            }
        } catch (err) {
            console.error('Error fetching previous period data:', err);
            setPreviousPeriodData({});
        }
    }, [currentUser, selectedBrand, selectedPlatform, dateRange]);

    // Fetch aggregate metrics
    const fetchAggregateMetrics = useCallback(async () => {
        if (!currentUser || !selectedBrand || !selectedPlatform) return;

        try {
            const params = new URLSearchParams({
                userId: currentUser.uid,
                brandId: selectedBrand.id,
                platform: selectedPlatform,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
            });

            const response = await fetch(`${API_BASE}/api/metrics/aggregate?${params}`);

            if (response.ok) {
                const data = await response.json();
                setAggregateMetrics(data.metrics || null);
            } else {
                // API not available - use default empty metrics
                setAggregateMetrics({ spend: 0, revenue: 0, impressions: 0, clicks: 0, conversions: 0, roas: 0 });
            }
        } catch (err) {
            console.error('Error fetching aggregate metrics:', err);
            setAggregateMetrics({ spend: 0, revenue: 0, impressions: 0, clicks: 0, conversions: 0, roas: 0 });
        }
    }, [currentUser, selectedBrand, selectedPlatform, dateRange]);

    // Fetch previous period aggregate metrics
    const fetchPreviousAggregateMetrics = useCallback(async () => {
        if (!currentUser || !selectedBrand || !selectedPlatform) return;

        try {
            const currentStart = new Date(dateRange.startDate);
            const currentEnd = new Date(dateRange.endDate);
            const durationMs = currentEnd - currentStart;

            const previousEnd = new Date(currentStart);
            previousEnd.setDate(previousEnd.getDate() - 1);
            const previousStart = new Date(previousEnd - durationMs);

            const formatDate = (date) => date.toISOString().split('T')[0];

            const params = new URLSearchParams({
                userId: currentUser.uid,
                brandId: selectedBrand.id,
                platform: selectedPlatform,
                startDate: formatDate(previousStart),
                endDate: formatDate(previousEnd)
            });

            const response = await fetch(`${API_BASE}/api/metrics/aggregate?${params}`);

            if (response.ok) {
                const data = await response.json();
                setPreviousAggregateMetrics(data.metrics || null);
            } else {
                // API not available - use null for previous metrics (no comparison)
                setPreviousAggregateMetrics(null);
            }
        } catch (err) {
            console.error('Error fetching previous aggregate metrics:', err);
            setPreviousAggregateMetrics(null);
        }
    }, [currentUser, selectedBrand, selectedPlatform, dateRange]);

    // Fetch monthly budget tracking
    const fetchMonthlyBudget = useCallback(async () => {
        if (!currentUser || !selectedBrand) return;

        try {
            const now = new Date();
            const params = new URLSearchParams({
                userId: currentUser.uid,
                brandId: selectedBrand.id,
                month: now.getMonth() + 1,
                year: now.getFullYear()
            });

            const response = await fetch(`${API_BASE}/api/budget/monthly?${params}`);

            if (response.ok) {
                const data = await response.json();
                setMonthlyBudget(data.budget || null);
            } else {
                // API not available - use null (budget section won't render)
                setMonthlyBudget(null);
            }
        } catch (err) {
            console.error('Error fetching monthly budget:', err);
            setMonthlyBudget(null);
        }
    }, [currentUser, selectedBrand]);

    // Initial data load
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setError(null);

            await fetchPlatformConnections();
        };

        if (currentUser && selectedBrand) {
            loadData();
        }
    }, [currentUser, selectedBrand, fetchPlatformConnections]);

    // Load campaign data when platform is selected
    useEffect(() => {
        const loadCampaignData = async () => {
            if (!selectedPlatform) return;

            setIsLoading(true);
            setError(null);

            try {
                await Promise.all([
                    fetchCampaigns(),
                    fetchPreviousPeriodData(),
                    fetchAggregateMetrics(),
                    fetchPreviousAggregateMetrics(),
                    fetchMonthlyBudget()
                ]);
            } catch (err) {
                setError('Failed to load data');
            } finally {
                setIsLoading(false);
            }
        };

        loadCampaignData();
    }, [
        selectedPlatform,
        dateRange,
        fetchCampaigns,
        fetchPreviousPeriodData,
        fetchAggregateMetrics,
        fetchPreviousAggregateMetrics,
        fetchMonthlyBudget
    ]);

    // Refresh all data from cache
    const refreshData = useCallback(async () => {
        setIsRefreshing(true);
        setError(null);

        try {
            await Promise.all([
                fetchPlatformConnections(),
                fetchCampaigns(),
                fetchPreviousPeriodData(),
                fetchAggregateMetrics(),
                fetchPreviousAggregateMetrics(),
                fetchMonthlyBudget()
            ]);
        } catch (err) {
            setError('Failed to refresh data');
        } finally {
            setIsRefreshing(false);
        }
    }, [
        fetchPlatformConnections,
        fetchCampaigns,
        fetchPreviousPeriodData,
        fetchAggregateMetrics,
        fetchPreviousAggregateMetrics,
        fetchMonthlyBudget
    ]);

    // Sync campaigns from ad platforms (forces fresh fetch from Meta/Google APIs)
    const syncCampaigns = useCallback(async () => {
        if (!currentUser || !selectedBrand) return;

        setIsRefreshing(true);
        setError(null);

        try {
            // Call the fetch-data endpoint which pulls fresh data from ad platforms
            const response = await fetch(`${API_BASE}/api/campaigns/fetch-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    brandId: selectedBrand.id,
                    forceRefresh: true,
                    dateRange: {
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate
                    }
                })
            });

            if (response.ok) {
                const data = await response.json();
                console.log('[CommandCenter] Synced campaigns:', data.campaigns?.length || 0);

                // Normalize campaigns with objectives
                const campaignsWithObjective = (data.campaigns || []).map(campaign => ({
                    ...campaign,
                    normalizedObjective: normalizeObjective(campaign.objective)
                }));
                setCampaigns(campaignsWithObjective);

                // Also refresh aggregate metrics
                await Promise.all([
                    fetchAggregateMetrics(),
                    fetchPreviousAggregateMetrics()
                ]);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to sync campaigns');
            }
        } catch (err) {
            console.error('Error syncing campaigns:', err);
            setError(err.message || 'Failed to sync campaigns');
        } finally {
            setIsRefreshing(false);
        }
    }, [currentUser, selectedBrand, dateRange, fetchAggregateMetrics, fetchPreviousAggregateMetrics]);

    // Get selected campaign object
    const selectedCampaign = useMemo(() => {
        if (!selectedCampaignId) return null;
        return campaigns.find(c => c.id === selectedCampaignId) || null;
    }, [selectedCampaignId, campaigns]);

    // Filter campaigns by current platform
    const filteredCampaigns = useMemo(() => {
        if (!selectedPlatform) return campaigns;
        return campaigns.filter(c =>
            c.platform?.toLowerCase() === selectedPlatform.toLowerCase()
        );
    }, [campaigns, selectedPlatform]);

    // Generate insights for current campaigns
    const insights = useMemo(() => {
        return generateInsights(filteredCampaigns, previousPeriodData);
    }, [filteredCampaigns, previousPeriodData]);

    // Build campaign knowledge base for AI advisor (memoized for performance)
    const campaignKnowledgeBase = useMemo(() => {
        return buildCampaignKnowledgeBase(filteredCampaigns, previousPeriodData);
    }, [filteredCampaigns, previousPeriodData]);

    // Calculate aggregate trends
    const aggregateTrends = useMemo(() => {
        if (!aggregateMetrics || !previousAggregateMetrics) return {};

        return {
            spend: calculateTrend(aggregateMetrics.spend || 0, previousAggregateMetrics.spend || 0),
            revenue: calculateTrend(aggregateMetrics.revenue || 0, previousAggregateMetrics.revenue || 0),
            roas: calculateTrend(aggregateMetrics.roas || 0, previousAggregateMetrics.roas || 0),
            impressions: calculateTrend(aggregateMetrics.impressions || 0, previousAggregateMetrics.impressions || 0),
            clicks: calculateTrend(aggregateMetrics.clicks || 0, previousAggregateMetrics.clicks || 0),
            conversions: calculateTrend(aggregateMetrics.conversions || 0, previousAggregateMetrics.conversions || 0)
        };
    }, [aggregateMetrics, previousAggregateMetrics]);

    // Count alerts per platform
    const platformAlertCounts = useMemo(() => {
        const counts = {};
        insights.forEach(insight => {
            const platform = insight.platform?.toLowerCase();
            if (platform) {
                counts[platform] = (counts[platform] || 0) + 1;
            }
        });
        return counts;
    }, [insights]);

    // Connected platforms list
    const connectedPlatforms = useMemo(() => {
        return platformConnections
            .filter(conn => conn.connected || conn.status === 'connected' || conn.status === 'active')
            .map(conn => ({
                ...conn,
                alertCount: platformAlertCounts[conn.platform?.toLowerCase()] || 0
            }));
    }, [platformConnections, platformAlertCounts]);

    // Toggle section expansion
    const toggleSection = useCallback((section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }, []);

    // Handle campaign selection
    const selectCampaign = useCallback((campaignId) => {
        setSelectedCampaignId(campaignId);
        // Reset expanded sections when selecting new campaign
        setExpandedSections({
            audiences: false,
            creatives: false,
            placements: false,
            scheduledChanges: false,
            googleAds: false
        });
    }, []);

    // Clear campaign selection
    const clearSelection = useCallback(() => {
        setSelectedCampaignId(null);
    }, []);

    // Handle platform change
    const changePlatform = useCallback((platform) => {
        setSelectedPlatform(platform);
        setSelectedCampaignId(null); // Clear selection when changing platform
    }, []);

    // Load advisor chat from Firebase
    const loadAdvisorChat = useCallback(async () => {
        if (!currentUser?.uid || !selectedBrand?.id) return;

        setAdvisorLoading(true);
        try {
            const chat = await loadLatestAdvisorChat(currentUser.uid, selectedBrand.id);
            if (chat) {
                setAdvisorChatId(chat.id);
                setAdvisorMessages(chat.messages || []);
                setAdvisorGoal(chat.userGoal || null);
            }
        } catch (err) {
            console.error('Failed to load advisor chat:', err);
        } finally {
            setAdvisorLoading(false);
        }
    }, [currentUser?.uid, selectedBrand?.id]);

    // Save advisor chat to Firebase
    const saveAdvisorChatToFirebase = useCallback(async (messages, goal, existingChatId) => {
        if (!currentUser?.uid || !selectedBrand?.id) return null;

        try {
            const chatData = prepareChatDataForSave({ messages, userGoal: goal });
            const savedChatId = await saveAdvisorChat(
                { ...chatData, id: existingChatId },
                currentUser.uid,
                selectedBrand.id
            );
            return savedChatId;
        } catch (err) {
            console.error('Failed to save advisor chat:', err);
            return null;
        }
    }, [currentUser?.uid, selectedBrand?.id]);

    // Update advisor messages and auto-save
    const updateAdvisorMessages = useCallback((newMessages) => {
        setAdvisorMessages(newMessages);
        // Auto-save with debounce would go here, but for now save immediately
        saveAdvisorChatToFirebase(newMessages, advisorGoal, advisorChatId)
            .then(savedId => {
                if (savedId && !advisorChatId) {
                    setAdvisorChatId(savedId);
                }
            });
    }, [advisorGoal, advisorChatId, saveAdvisorChatToFirebase]);

    // Update advisor goal
    const updateAdvisorGoal = useCallback((goal) => {
        setAdvisorGoal(goal);
    }, []);

    // Start new advisor chat
    const startNewAdvisorChat = useCallback(() => {
        setAdvisorMessages([]);
        setAdvisorGoal(null);
        setAdvisorChatId(null);
    }, []);

    // Load advisor chat when brand changes
    useEffect(() => {
        if (currentUser?.uid && selectedBrand?.id) {
            loadAdvisorChat();
        } else {
            // Reset chat state when no brand is selected
            setAdvisorMessages([]);
            setAdvisorGoal(null);
            setAdvisorChatId(null);
        }
    }, [currentUser?.uid, selectedBrand?.id, loadAdvisorChat]);

    const value = {
        // Selection
        selectedCampaignId,
        selectedCampaign,
        selectCampaign,
        clearSelection,
        selectedPlatform,
        changePlatform,

        // Date range
        dateRange,
        dateRangePreset,
        setDateRangePreset,

        // Data
        campaigns: filteredCampaigns,
        allCampaigns: campaigns,
        previousPeriodData,
        platformConnections,
        connectedPlatforms,
        aggregateMetrics,
        previousAggregateMetrics,
        aggregateTrends,
        monthlyBudget,
        insights,
        platformAlertCounts,
        campaignKnowledgeBase,

        // Loading states
        isLoading,
        isRefreshing,
        error,

        // Actions
        refreshData,
        syncCampaigns,

        // Section expansion
        expandedSections,
        toggleSection,

        // AI Advisor chat (shared state)
        advisorMessages,
        advisorGoal,
        advisorChatId,
        advisorLoading,
        updateAdvisorMessages,
        updateAdvisorGoal,
        startNewAdvisorChat,
        loadAdvisorChat,
        setAdvisorChatId
    };

    return (
        <CommandCenterContext.Provider value={value}>
            {children}
        </CommandCenterContext.Provider>
    );
};

export default CommandCenterContext;
